/**
 * AuditEventRow - one row in the AdminAuditPage table (Phase 12.4).
 *
 * Columns: timestamp, ticket_id (UUID, mono), actor (UUID for now -
 * resolution to name TODO once a join endpoint exists), event_type,
 * from -> to value diff display.
 *
 * Per SPEC §9 the ticket id should link to ``/tickets/{ticket_number}``,
 * but the audit endpoint only returns the ticket UUID. Resolving the
 * UUID to a number needs a separate endpoint; tagged TODO.
 */
import { Badge } from "@/components/ui";
import type { TicketEventResponse } from "@/types/api";

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString();
}

function formatActorRef(row: TicketEventResponse): string {
  // TODO(phase 12 follow-up): resolve actor_user_id / actor_api_key_id to
  // a human-readable name via a future /admin/audit endpoint expansion.
  // For now show the raw UUID so investigations can paste it into a
  // user-detail page.
  if (row.actor_user_id) return row.actor_user_id;
  if (row.actor_api_key_id) return row.actor_api_key_id;
  return "-";
}

export interface AuditEventRowProps {
  row: TicketEventResponse;
}

export function AuditEventRow({ row }: AuditEventRowProps): JSX.Element {
  return (
    <tr className="hover:bg-neutral-50">
      <td className="whitespace-nowrap px-3 py-2 text-xs text-neutral-500">
        {formatTimestamp(row.created_at)}
      </td>
      <td className="px-3 py-2 font-mono text-[11px] text-neutral-700">
        {/* TODO(phase 12 follow-up): swap for a Link to /tickets/{number}
            once the audit endpoint includes ticket_number. */}
        <span title={row.ticket_id}>{row.ticket_id.slice(0, 8)}</span>
      </td>
      <td className="px-3 py-2 font-mono text-[11px] text-neutral-700">
        <span title={formatActorRef(row)}>
          {formatActorRef(row).slice(0, 8)}
        </span>
      </td>
      <td className="px-3 py-2 text-xs">
        <Badge variant="default">{row.actor_kind}</Badge>
      </td>
      <td className="px-3 py-2 text-xs font-medium text-neutral-700">
        {row.event_type}
      </td>
      <td className="px-3 py-2 text-xs text-neutral-700">
        <div className="flex flex-col gap-1">
          {row.from_value && (
            <pre className="max-h-32 overflow-auto rounded bg-neutral-100 p-1.5 text-[11px]">
              {JSON.stringify(row.from_value, null, 2)}
            </pre>
          )}
          {row.to_value && (
            <pre className="max-h-32 overflow-auto rounded bg-primary-50 p-1.5 text-[11px]">
              {JSON.stringify(row.to_value, null, 2)}
            </pre>
          )}
          {!row.from_value && !row.to_value && (
            <span className="text-neutral-300">-</span>
          )}
        </div>
      </td>
    </tr>
  );
}

export default AuditEventRow;
